import { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, Save, X, Tag } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { categoriesAPI } from '../../services/api';
import { toast } from 'sonner';

const slugify = (s) =>
  s.toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const CategoriesAdmin = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ id: '', name: '', description: '' });
  const [autoSlug, setAutoSlug] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      setCategories(await categoriesAPI.getAll());
    } catch (e) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({ id: '', name: '', description: '' });
    setAutoSlug(true);
    setCreating(true);
  };

  const handleCreate = async () => {
    if (!form.id || !form.name) {
      toast.error('Slug and name are required');
      return;
    }
    try {
      await categoriesAPI.create(form);
      toast.success('Category created');
      setCreating(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleSaveEdit = async () => {
    try {
      const { id, ...payload } = editing;
      await categoriesAPI.update(id, payload);
      toast.success('Category updated');
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete category "${id}"? Products using it must be reassigned first.`)) return;
    try {
      await categoriesAPI.delete(id);
      toast.success('Category deleted');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="space-y-4" data-testid="categories-admin">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold">Categories</h2>
          <p className="text-sm text-gray-500">{categories.length} categories</p>
        </div>
        <Button
          size="lg"
          onClick={openCreate}
          data-testid="add-category-btn"
          className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
        >
          <Plus className="mr-2 h-5 w-5" /> Add Category
        </Button>
      </div>

      {loading ? (
        <p className="text-gray-500 text-center py-10">Loading…</p>
      ) : categories.length === 0 ? (
        <p className="text-gray-500 text-center py-10">No categories yet.</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {categories.map((c) => (
            <Card key={c.id} data-testid={`category-${c.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    <Tag className="h-4 w-4 mt-1 text-blue-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{c.name}</p>
                      <p className="text-xs text-gray-500 font-mono truncate">{c.id}</p>
                      {c.description && <p className="text-sm text-gray-600 mt-1">{c.description}</p>}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...c })} data-testid={`edit-cat-${c.id}`}>
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(c.id)} data-testid={`delete-cat-${c.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create modal */}
      {creating && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setCreating(false)}>
          <Card className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-6 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Add Category</h3>
                <button onClick={() => setCreating(false)} aria-label="Close"><X className="h-5 w-5" /></button>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Display Name *</label>
                <Input
                  value={form.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setForm((p) => ({ ...p, name, id: autoSlug ? slugify(name) : p.id }));
                  }}
                  placeholder="e.g. Hybrid Tuning"
                  data-testid="category-form-name"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Slug / ID *</label>
                <Input
                  value={form.id}
                  onChange={(e) => { setAutoSlug(false); setForm((p) => ({ ...p, id: slugify(e.target.value) })); }}
                  placeholder="e.g. hybrid-tuning"
                  data-testid="category-form-id"
                />
                <p className="text-xs text-gray-400 mt-1">Used internally and in URLs. Lowercase, dashes only.</p>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="w-full p-2 border rounded text-sm"
                  placeholder="Short description (optional)"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
                <Button onClick={handleCreate} data-testid="create-category-submit">
                  <Save className="h-4 w-4 mr-1" /> Create Category
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <Card className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-6 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Edit Category</h3>
                <button onClick={() => setEditing(null)} aria-label="Close"><X className="h-5 w-5" /></button>
              </div>
              <p className="text-xs text-gray-500">Slug: <span className="font-mono">{editing.id}</span> (cannot be changed)</p>
              <div>
                <label className="block text-xs font-medium mb-1">Display Name *</label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Description</label>
                <textarea
                  value={editing.description || ''}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  rows={3}
                  className="w-full p-2 border rounded text-sm"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={handleSaveEdit} data-testid="save-cat-btn">
                  <Save className="h-4 w-4 mr-1" /> Save
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CategoriesAdmin;
